"use client"

import type React from "react"
import { useState } from "react"
import { ChevronDown } from "lucide-react"

interface FAQTab {
  id: number
  question: string
  answer: string
  icon: JSX.Element
}

const defaultIcon = (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
)

const faqTabs: FAQTab[] = [
  {
    id: 1,
    question: "What is teleconsultation?",
    answer:
      "Teleconsultation lets you connect with a doctor remotely via video call. You receive medical advice, diagnosis and treatment from the comfort of your home using your smartphone, tablet or computer with internet access.",
    icon: defaultIcon,
  },
  {
    id: 2,
    question: "How do I book a consultation?",
    answer:
      "Visit the doctor’s profile on Prescrable, choose an available time slot and submit your booking request. Your chosen doctor will review and accept or decline the appointment. You’ll receive a confirmation message once it’s accepted.",
    icon: defaultIcon,
  },
  {
    id: 3,
    question: "How will I know if my appointment is confirmed?",
    answer:
      "You’ll receive a confirmation via WhatsApp, SMS or email after the doctor approves your requested time slot. If you don’t receive any notification within a reasonable time, check your booking status in the app or contact the doctor via the number provided.",
    icon: defaultIcon,
  },
  {
    id: 4,
    question: "Do I need to pay before the consultation?",
    answer:
      "Yes. Once your appointment is accepted, you’ll receive the doctor’s UPI ID and a contact number via WhatsApp. You must transfer your consultation fee to this UPI ID before the call starts.",
    icon: defaultIcon,
  },
  {
    id: 5,
    question: "Why do I receive the doctor’s UPI ID and contact number?",
    answer:
      "These details let you pay the doctor directly and reach out if you have issues. Payments are made directly to the doctor, so Prescrable does not handle your funds or store your payment details. The phone number is shared so you can coordinate if there’s a delay or technical problem.",
    icon: defaultIcon,
  },
  {
    id: 6,
    question: "How do I know the doctor has received my payment?",
    answer:
      "After you transfer the fee via UPI, you should see a confirmation in your payment app. The doctor will also verify the payment receipt before starting the consultation. If you’re unsure, you can share a screenshot of your payment confirmation with the doctor.",
    icon: defaultIcon,
  },
  {
    id: 7,
    question: "Do I need to be available at specific times?",
    answer:
      "Yes. It’s best to be ready a few minutes before your scheduled time. We recommend joining the call two minutes early and staying for up to five minutes after the start time in case of minor delays.",
    icon: defaultIcon,
  },
  {
    id: 8,
    question: "What if the doctor doesn’t show up or is late?",
    answer:
      "If the doctor is delayed, they may contact you via the phone number you provided. If you haven’t heard from the doctor within a few minutes, you can reach out to the doctor using the contact number included in your booking confirmation.",
    icon: defaultIcon,
  },
  {
    id: 9,
    question: "Can I reschedule or cancel my appointment?",
    answer:
      "Yes. If you need to reschedule or cancel, contact the doctor directly using the phone number provided. Because you pay the doctor directly, any changes to your booking (including refunds) must be handled with the doctor.",
    icon: defaultIcon,
  },
  {
    id: 10,
    question: "What happens if I need a refund or if there’s a payment issue?",
    answer:
      "Since your payment goes straight to the doctor’s UPI, the doctor is responsible for any refund or payment corrections. Prescrable does not hold your funds and cannot issue refunds on the doctor’s behalf.",
    icon: defaultIcon,
  },
  {
    id: 11,
    question: "Who ensures the quality of the consultation?",
    answer:
      "The doctor providing your consultation is responsible for its quality. Prescrable facilitates the video connection, but it does not provide medical advice or oversee the doctor’s decisions.",
    icon: defaultIcon,
  },
  {
    id: 12,
    question: "Is my personal data secure?",
    answer:
      "Yes. Prescrable uses secure connections and follows telemedicine guidelines to protect your personal and medical information.",
    icon: defaultIcon,
  },
  {
    id: 13,
    question: "Is it safe to pay via UPI?",
    answer:
      "UPI payments are handled through your bank’s secure interface. Prescrable does not store your payment details. Always confirm the UPI ID and pay directly through your verified UPI app.",
    icon: defaultIcon,
  },
  {
    id: 14,
    question: "Do I need to install an app or have special equipment?",
    answer:
      "You can access Prescrable via a web browser. You need a device with a camera and microphone and a stable internet connection. There’s no special equipment required beyond a smartphone or computer.",
    icon: defaultIcon,
  },
  {
    id: 15,
    question: "What if I miss the consultation?",
    answer:
      "If you miss your session, contact the doctor using the phone number provided to explain your situation. It’s up to you and the doctor to decide whether to reschedule or request a refund, since the payment is between you and the doctor.",
    icon: defaultIcon,
  },
  {
    id: 16,
    question: "How do I get my prescription?",
    answer:
      "If the doctor issues a prescription after your consultation, you’ll receive it digitally via email, WhatsApp or within the platform. Prescrable ensures this process is secure and compliant with regulations.",
    icon: defaultIcon,
  },
  {
    id: 17,
    question: "Do I need to pay again for follow-up consultations?",
    answer:
      "Follow-up policies vary by doctor. You may need to pay for each consultation unless the doctor offers a separate arrangement. Discuss any follow-up fees directly with your doctor.",
    icon: defaultIcon,
  },
  {
    id: 18,
    question: "Can I contact customer support if I need help?",
    answer:
      "Yes. If you encounter technical issues or general questions about the platform, contact Prescrable’s support team through the help section or the contact details provided on the platform.",
    icon: defaultIcon,
  },
]

const FaqTeleconsultation: React.FC = () => {
  const [activeTab, setActiveTab] = useState<number>(0)

  return (
    <section id="faqs" className="py-10">
      {/* REMOVE SIDE PADDING on mobile */}
      <div className="container mx-auto px-0 sm:px-4">
        <div className="max-w-5xl mx-auto">

          <div className="text-center mb-10">
            <h2 className="mb-2 font-bold">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto font-roboto">
              Find answers to common questions about teleconsultation on Prescrable.
            </p>
          </div>

          <div className="space-y-4">
            {faqTabs.map((tab) => (
              <div
                key={tab.id}
                className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 ${activeTab === tab.id ? "ring-1 ring-gray-200" : "hover:shadow-md"
                  }`}
              >
                {/* MOBILE PADDING REDUCED */}
                <button
                  className="w-full flex items-center justify-between p-4 sm:p-5 text-left"
                  onClick={() => setActiveTab(activeTab === tab.id ? 0 : tab.id)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full ${activeTab === tab.id
                        ? "bg-gray-400 text-white"
                        : "bg-gray-100 text-gray-400"
                        } transition-colors duration-300`}
                    >
                      {tab.icon}
                    </div>
                    <h3 className="font-semibold text-gray-800 font-nunito">
                      {tab.question}
                    </h3>
                  </div>

                  <ChevronDown
                    className={`w-5 h-5 text-gray-500 transition-transform duration-300 ${activeTab === tab.id ? "rotate-180" : ""
                      }`}
                  />
                </button>

                <div
                  className={`transition-all duration-300 ease-in-out overflow-hidden ${activeTab === tab.id ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                    }`}
                >
                  <div className="p-4 sm:p-5 pt-0 border-t border-gray-100">
                    <p className="text-gray-700 font-roboto leading-relaxed whitespace-pre-line">
                      {tab.answer}
                    </p>
                  </div>
                </div>

              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default FaqTeleconsultation
